import { Box, Button, Center, Link } from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { logger } from '../../../services/sentry';
import { customerReferrer } from '../../../store/atom/referrer';
import {
  FINIX_APPLICATION_ID,
  FINIX_ENVIRONMENT,
  getFinixCardTokenForm,
  paymentFormOptions,
} from '../../../utils/finix-payout';
import { FinixPaymentInstrumentToken } from '../../../utils/types/referrer';

const FINIX_FORM_ID = 'finix-card-payment-dashboard-form';

type Props = {
  defaultValues: {
    name: string;
    address_line1: string;
    address_city: string;
    address_state: string;
    address_postal_code: string;
    address_country?: string;
  };
  setFinixForm: Dispatch<SetStateAction<unknown>>;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  finixForm: any;
  createToken: (fields: FinixPaymentInstrumentToken) => Promise<any>;
  verifyInstrument: (id?: string) => Promise<void>;
  createMerchant: () => Promise<void>;
  cancel?: () => void;
};

const PayoutAccountDetails = ({
  defaultValues,
  finixForm,
  setFinixForm,
  createToken,
  verifyInstrument,
  createMerchant,
  cancel,
}: Props) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const referrer = useAtomValue(customerReferrer);

  const handleInstrument = async (data: { identity: string; token: string }) => {
    if (!referrer.finix_identity) return;
    try {
      const id = await createToken({
        identity: data.identity,
        token: data.token,
      });
      if (id) {
        await verifyInstrument(id);
        await createMerchant();
      }
    } catch (err) {
      logger.error('PayoutAccountDetails', 'handleInstrument failed', err, null);
    } finally {
      setIsProcessing(false);
    }
  };

  const processPayment = async () => {
    setIsProcessing(true);
    // eslint-disable-next-line react/prop-types
    finixForm.submit(
      FINIX_ENVIRONMENT,
      FINIX_APPLICATION_ID,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (err: any, res?: { data: { id: string } }) => {
        if (err) {
          logger.error('PayoutAccountDetails', 'Finix form submission failed', err, null);
          setIsProcessing(false);
          return;
        }
        if (!res || !res.data.id) {
          logger.error('PayoutAccountDetails', 'Missing token in Finix response', res, null);
          setIsProcessing(false);
          return;
        }
        const token = res.data.id;
        handleInstrument({
          identity: referrer?.finix_identity || '',
          token,
        });
      },
    );
  };

  useEffect(() => {
    const form = getFinixCardTokenForm(FINIX_FORM_ID, {
      ...paymentFormOptions,
      ...{ defaultValues },
    });
    setFinixForm(form);
    // Re-initialize when deal data loads (name is the reliable signal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues.name]);

  return (
    <Box>
      <Box
        sx={{
          label: {
            fontSize: '1rem',
            marginBottom: '5px',
            textTransform: 'uppercase',
            color: 'leaseEndBlue',
            fontWeight: 600,
            letterSpacing: '1px',
          },
        }}
        textAlign="left"
        id={FINIX_FORM_ID}
      />

      <Center flexDir="column">
        <Button
          px={0}
          mt={8}
          w="200px"
          isLoading={isProcessing}
          loadingText="PROCESSING"
          onClick={processPayment}
        >
          Save
        </Button>
        {cancel && <Link onClick={cancel}>cancel</Link>}
      </Center>
    </Box>
  );
};

export default PayoutAccountDetails;
